/**
 * A delete that asks once.
 *
 * Two taps rather than a dialog: a browser `confirm()` in an installed PWA looks
 * like the browser talking, not the app, and on a phone it lands under your
 * thumb. The second tap is deliberately a different word — "zeker?" — so it does
 * not read as the same button twice.
 *
 * It backs out by itself after a few seconds. A half-armed delete sitting on the
 * screen is the one that gets pressed by accident later.
 */

import { useEffect, useState } from 'react';

const ARMED_MS = 4000;

export default function DeleteButton({
  onDelete,
  label = 'Verwijderen',
  busyLabel = 'Bezig…',
  className = '',
}: {
  onDelete: () => Promise<void> | void;
  label?: string;
  busyLabel?: string;
  className?: string;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), ARMED_MS);
    return () => window.clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        setBusy(true);
        void Promise.resolve(onDelete()).finally(() => setBusy(false));
      }}
      className={`text-sm ${armed ? 'font-medium text-bad' : 'text-slate-500 hover:text-white'} ${className}`}
    >
      {busy ? busyLabel : armed ? 'zeker?' : label}
    </button>
  );
}
